import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Zap, Plus, Sparkles, Paperclip, X, Loader2, Mic, MicOff } from 'lucide-react';
import { dietApi } from '../api/diet';
import { formatAmountUnit } from '../lib/formatAmountUnit';
import i18n from '../i18n';

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_EDGE = 1600;

async function compressImageToBase64(file) {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bitmap, 0, 0, w, h);
  const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
  return dataUrl.replace(/^data:image\/\w+;base64,/, '');
}

export default function QuickAddMealCard({ onLog, disabled }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [calories, setCalories] = useState('');
  const [protein, setProtein] = useState('');
  const [carbs, setCarbs] = useState('');
  const [fat, setFat] = useState('');
  const [amount, setAmount] = useState('');
  const [unit, setUnit] = useState('');
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);

  // Multi-item review state (v3.8.59): when AI decomposes input into 2+ items,
  // we bypass the single-row form and show a checkbox list instead.
  // [{ name, calories, protein_g, carbs_g, fat_g, selected }]
  const [parsedItems, setParsedItems] = useState([]);

  const [description, setDescription] = useState('');
  const [imageBase64, setImageBase64] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  const fileInputRef = useRef(null);
  const recognitionRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);
  const speechSupported = typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  const numOr0 = (v) => {
    const n = parseFloat(String(v).replace(',', '.'));
    return isNaN(n) || n < 0 ? 0 : n;
  };

  const canSubmit = name.trim().length > 0 && String(calories).trim() !== '';
  const canParse = description.trim().length > 0 || !!imageBase64;
  const multiMode = parsedItems.length >= 2;
  const selectedItemCount = parsedItems.filter(it => it.selected).length;

  const reset = () => {
    setName('');
    setCalories('');
    setProtein('');
    setCarbs('');
    setFat('');
    setAmount('');
    setUnit('');
    setSaveAsTemplate(false);
    setDescription('');
    setImageBase64('');
    setImagePreview('');
    setParsedItems([]);
  };

  const parseMutation = useMutation({
    mutationFn: async () => {
      const { data } = await dietApi.parseMealWithAI({
        description: description.trim(),
        imageData: imageBase64,
        language: (i18n.language || 'en').startsWith('pt') ? 'pt' : 'en',
      });
      return data;
    },
    onSuccess: (data) => {
      const items = Array.isArray(data.items) ? data.items : [];
      if (items.length >= 2) {
        // Multi-item: defer to review UI, clear the single-row fields
        setParsedItems(items.map(it => ({ ...it, selected: true })));
        setName('');
        setCalories('');
        setProtein('');
        setCarbs('');
        setFat('');
        setAmount('');
        setUnit('');
      } else {
        // Single-item (or empty): populate the existing simple form
        const one = items[0] || data;
        setParsedItems([]);
        setName(one.name || '');
        setCalories(one.calories != null ? String(one.calories) : '');
        setProtein(one.protein_g != null ? String(one.protein_g) : '');
        setCarbs(one.carbs_g != null ? String(one.carbs_g) : '');
        setFat(one.fat_g != null ? String(one.fat_g) : '');
        setAmount(one.amount != null ? String(one.amount) : '');
        setUnit(one.unit || '');
      }
    },
    onError: () => {
      toast.error(t('diet.quickAdd.parseError'));
    },
  });

  const toggleParsedItem = (idx) => {
    setParsedItems(prev => prev.map((it, i) => i === idx ? { ...it, selected: !it.selected } : it));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error(t('diet.quickAdd.imageTooLarge'));
      return;
    }
    try {
      const b64 = await compressImageToBase64(file);
      setImageBase64(b64);
      setImagePreview(`data:image/jpeg;base64,${b64}`);
    } catch {
      toast.error(t('diet.quickAdd.parseError'));
    }
  };

  const clearImage = () => {
    setImageBase64('');
    setImagePreview('');
  };

  const startRecording = () => {
    if (!speechSupported) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = true;
    rec.lang = (i18n.language || 'en').startsWith('pt') ? 'pt-PT' : 'en-US';

    const startingText = description;
    rec.onresult = (e) => {
      let interim = '';
      let finalText = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) finalText += r[0].transcript;
        else interim += r[0].transcript;
      }
      const sep = startingText.trim() ? ' ' : '';
      setDescription((startingText + sep + finalText + interim).trim());
    };
    rec.onerror = (e) => {
      setIsRecording(false);
      if (e.error !== 'aborted') {
        toast.error(t('diet.quickAdd.voiceError'));
      }
    };
    rec.onend = () => setIsRecording(false);

    try {
      rec.start();
      recognitionRef.current = rec;
      setIsRecording(true);
    } catch {
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    try { recognitionRef.current?.stop(); } catch { /* ignore */ }
    setIsRecording(false);
  };

  const toggleRecording = () => isRecording ? stopRecording() : startRecording();

  const buildPortionFields = (amt, un) => {
    const a = numOr0(amt);
    const u = String(un || '').trim();
    if (a > 0) {
      return { quantity: a, unit: u || 'g', serving_size: a, serving_unit: u || 'g', servings: 1, serving_description: '' };
    }
    return { quantity: 1, unit: 'serving', servings: 1 };
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    const entry = {
      food_name: name.trim(),
      calories: numOr0(calories),
      protein: numOr0(protein),
      carbs: numOr0(carbs),
      fat: numOr0(fat),
      ...buildPortionFields(amount, unit),
      is_custom: true,
    };
    onLog({ entry, saveAsTemplate });
    reset();
  };

  const handleSubmitMultiple = () => {
    if (selectedItemCount === 0) return;
    const entries = parsedItems.filter(it => it.selected).map(it => ({
      food_name: it.name,
      calories: numOr0(it.calories),
      protein: numOr0(it.protein_g),
      carbs: numOr0(it.carbs_g),
      fat: numOr0(it.fat_g),
      ...buildPortionFields(it.amount, it.unit),
      is_custom: true,
    }));
    onLog({ entries });  // saveAsTemplate intentionally omitted — N>1 templating is a future feature
    reset();
  };

  return (
    <Card className="border-primary/40 mb-3" data-testid="quick-add-meal-card">
      <CardContent className="p-3 space-y-2">
        <div className="flex items-center gap-1.5">
          <Zap className="h-4 w-4 text-primary" />
          <span className="font-semibold text-sm">{t('diet.quickAdd.title')}</span>
        </div>
        <p className="text-xs text-muted-foreground">{t('diet.quickAdd.subtitle')}</p>

        {/* AI input — textarea + image attach + parse */}
        <div className="space-y-2">
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('diet.quickAdd.describePlaceholder')}
            rows={2}
            className="text-sm resize-none"
            data-testid="quick-add-description"
          />
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileChange}
              className="hidden"
              data-testid="quick-add-file-input"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-10 md:h-9 shrink-0"
              onClick={() => fileInputRef.current?.click()}
              aria-label={t('diet.quickAdd.attachImage')}
              data-testid="quick-add-attach-btn"
            >
              <Paperclip className="h-4 w-4" />
            </Button>
            {speechSupported && (
              <Button
                type="button"
                variant={isRecording ? 'destructive' : 'outline'}
                size="sm"
                className={`h-10 md:h-9 shrink-0 ${isRecording ? 'animate-pulse' : ''}`}
                onClick={toggleRecording}
                aria-label={t(isRecording ? 'diet.quickAdd.stopVoice' : 'diet.quickAdd.startVoice')}
                data-testid="quick-add-mic-btn"
              >
                {isRecording ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </Button>
            )}
            {imagePreview && (
              <div className="relative shrink-0">
                <img
                  src={imagePreview}
                  alt=""
                  className="h-10 w-10 rounded object-cover border border-border"
                  data-testid="quick-add-image-preview"
                />
                <button
                  type="button"
                  onClick={clearImage}
                  aria-label={t('diet.quickAdd.removeImage')}
                  className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-destructive text-white flex items-center justify-center"
                  data-testid="quick-add-remove-image"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            <Button
              type="button"
              size="sm"
              className="flex-1 h-10 md:h-9 bg-primary hover:bg-primary/90"
              disabled={!canParse || parseMutation.isPending}
              onClick={() => parseMutation.mutate()}
              data-testid="quick-add-parse-btn"
            >
              {parseMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" /> {t('diet.quickAdd.parsing')}
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-1" /> {t('diet.quickAdd.parseWithAI')}
                </>
              )}
            </Button>
          </div>
        </div>

        {multiMode ? (
          <>
            {/* Multi-item review list */}
            <div className="rounded-lg border border-border/60 overflow-hidden mt-1">
              <div className="px-2.5 py-1.5 bg-muted/30 border-b border-border/50 flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('diet.quickAdd.detectedItems', { count: parsedItems.length })}
                </p>
                <p className="text-[11px] text-muted-foreground">{t('diet.photoReviewHint')}</p>
              </div>
              <div className="divide-y divide-border/30">
                {parsedItems.map((item, i) => (
                  <div key={i} className={`px-2.5 py-2 ${item.selected ? '' : 'opacity-50'}`} data-testid={`quick-add-item-${i}`}>
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={item.selected}
                        onChange={() => toggleParsedItem(i)}
                        className="mt-1 h-4 w-4 accent-primary cursor-pointer shrink-0"
                        aria-label={item.name}
                        data-testid={`quick-add-item-check-${i}`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium leading-snug">{item.name}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {item.amount != null && item.unit ? `${formatAmountUnit(item.amount, item.unit)} · ` : ''}{Math.round(item.calories)} kcal · P:{Math.round(item.protein_g)}g C:{Math.round(item.carbs_g)}g F:{Math.round(item.fat_g)}g
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Multi-item submit */}
            <Button
              onClick={handleSubmitMultiple}
              disabled={selectedItemCount === 0 || disabled}
              className="w-full h-10 md:h-9 bg-primary hover:bg-primary/90"
              data-testid="quick-add-submit-multi"
            >
              <Plus className="h-4 w-4 mr-1" />
              {t(selectedItemCount === 1 ? 'diet.logOneItem' : 'diet.logNItems', { count: selectedItemCount })}
            </Button>
          </>
        ) : (
          <>
            {/* Manual fields (also populated by single-item AI parse) */}
            <div className="space-y-2 pt-1">
              <div>
                <Label className="text-xs">{t('diet.quickAdd.name')}</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('diet.quickAdd.namePlaceholder')}
                  className="h-11 md:h-9 text-base md:text-sm"
                  data-testid="quick-add-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">{t('diet.quickAdd.amount')}</Label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder={t('diet.quickAdd.amountPlaceholder')}
                    className="h-11 md:h-9 text-base md:text-sm"
                    data-testid="quick-add-amount"
                  />
                </div>
                <div>
                  <Label className="text-xs">{t('diet.quickAdd.unit')}</Label>
                  <Input
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                    placeholder={t('diet.quickAdd.unitPlaceholder')}
                    maxLength={16}
                    className="h-11 md:h-9 text-base md:text-sm"
                    data-testid="quick-add-unit"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">{t('diet.quickAdd.calories')}</Label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    value={calories}
                    onChange={(e) => setCalories(e.target.value)}
                    className="h-11 md:h-9 text-base md:text-sm"
                    data-testid="quick-add-calories"
                  />
                </div>
                <div>
                  <Label className="text-xs">{t('diet.quickAdd.protein')}</Label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    value={protein}
                    onChange={(e) => setProtein(e.target.value)}
                    className="h-11 md:h-9 text-base md:text-sm"
                    data-testid="quick-add-protein"
                  />
                </div>
                <div>
                  <Label className="text-xs">{t('diet.quickAdd.carbs')}</Label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    value={carbs}
                    onChange={(e) => setCarbs(e.target.value)}
                    className="h-11 md:h-9 text-base md:text-sm"
                    data-testid="quick-add-carbs"
                  />
                </div>
                <div>
                  <Label className="text-xs">{t('diet.quickAdd.fat')}</Label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    value={fat}
                    onChange={(e) => setFat(e.target.value)}
                    className="h-11 md:h-9 text-base md:text-sm"
                    data-testid="quick-add-fat"
                  />
                </div>
              </div>
            </div>

            {/* Save-as-template checkbox */}
            <div className="flex items-center gap-2 pt-1">
              <Checkbox
                id="quick-add-save-template"
                checked={saveAsTemplate}
                onCheckedChange={(v) => setSaveAsTemplate(!!v)}
                data-testid="quick-add-save-template"
              />
              <Label htmlFor="quick-add-save-template" className="text-xs cursor-pointer">
                {t('diet.quickAdd.alsoSaveToQuickMeals')}
              </Label>
            </div>

            {/* Single-entry submit */}
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit || disabled}
              className="w-full h-10 md:h-9 bg-primary hover:bg-primary/90"
              data-testid="quick-add-submit"
            >
              <Plus className="h-4 w-4 mr-1" /> {t('diet.quickAdd.addToLog')}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
